"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { PACK_BY_ID, packOwningRoute, packsCoverRoute } from "@/lib/packs";
import { usePosSession } from "@/lib/hooks/usePosSession";

/**
 * A counter may only open the vertical it is standing in.
 *
 * SWEEP-1 S1. `/pos/context` has always answered which screens a counter has — `["checkout",
 * "members"]` for a gym, `["checkout", "front_desk"]` for a hotel — and **nothing read it**. A
 * search for `.screens` across this app returned no hits at all. Every one of the twenty-nine routes
 * rendered for every counter, each re-skinning the entire shell to its own vertical, so a hotel
 * front desk could open the salon calendar, the lab's sample board and the restaurant floor.
 *
 * That is the exact failure `TenantManifestPosContextService` warns about in its own comment — "a
 * flag on the context that nothing read" — inverted: the platform declared two screens and the
 * console showed twenty-nine.
 *
 * <h2>What is guarded, and what deliberately is not</h2>
 *
 * Only a vertical's OWN routes, resolved by {@link packOwningRoute} from the pack's home prefix. The
 * shared back office — customers, shifts, reports, settings, catalog, invoices, the group board —
 * belongs to no vertical and every counter keeps it. `checkout` is in every pack's screen list, so
 * it is shared by the same rule rather than by an exception.
 *
 * <h2>Why it redirects rather than showing a refusal</h2>
 *
 * There is nothing for the operator to do about it and nothing they did wrong: the counter is what
 * it is. A cashier who follows a stale bookmark should land on their own counter, not on a page
 * explaining a concept they did not ask about. Send them home.
 *
 * <h2>Why it waits</h2>
 *
 * While the context is loading the entitled packs are unknown, and guessing would either flash the
 * screen away from someone allowed to see it or let the wrong one paint. It renders children
 * untouched until the answer arrives; a counter with no context at all (an outage) is left alone,
 * because taking screens away from a till because a read failed is the wrong direction to fail.
 */
export function CounterScopeGuard({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { loading, packs, homePack, error } = usePosSession();

  const owner = pathname ? packOwningRoute(pathname) : undefined;
  const allowed = !owner || packs.length === 0 || packsCoverRoute(owner, packs);

  useEffect(() => {
    if (loading || error || allowed) return;
    router.replace(homePack ? PACK_BY_ID[homePack].home : "/");
  }, [loading, error, allowed, homePack, router]);

  return <>{children}</>;
}
